import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  getClients, 
  getServices, 
  getArticles,
  createQuote, 
  getQuote, 
  updateQuote 
} from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft, 
  Plus, 
  Trash2,
  Save
} from 'lucide-react';
import { toast } from 'sonner';
import { Client360Link } from '../../components/ui/client-360-link';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', {
    useGrouping: 'always', style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

export const QuoteFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    client_id: '',
    quote_number: '',
    issue_date: new Date().toISOString().split('T')[0],
    valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 21 }],
    notes: '',
    status: 'draft'
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [clientsRes, servicesRes, articlesRes] = await Promise.all([
        getClients().catch(() => ({ data: [] })),
        getServices().catch(() => ({ data: [] })),
        getArticles().catch(() => ({ data: [] }))
      ]);
      setClients(clientsRes.data || []);
      setServices(servicesRes.data || []);
      setArticles(articlesRes.data || []);

      if (isEditing) {
        const { data } = await getQuote(id);
        setFormData({
          client_id: data.client_id || '',
          quote_number: data.quote_number || '',
          issue_date: data.issue_date || new Date().toISOString().split('T')[0],
          valid_until: data.valid_until || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          items: data.items && data.items.length > 0 ? data.items : [{ description: '', quantity: 1, unit_price: 0, tax_rate: 21 }],
          notes: data.notes || '',
          status: data.status || 'draft'
        });
      }
    } catch (e) {
      console.error('Error loading quote form data:', e);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0, tax_rate: 21 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const addServiceToItems = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      setFormData({
        ...formData,
        items: [
          ...formData.items.filter(item => item.description),
          {
            service_id: service.id,
            description: service.name,
            quantity: 1,
            unit_price: service.hourly_rate,
            tax_rate: 21
          }
        ]
      });
    }
  };

  const addArticleToItems = (articleId) => {
    const article = articles.find(a => a.id === articleId);
    if (article) {
      setFormData({
        ...formData,
        items: [
          ...formData.items.filter(item => item.description),
          {
            article_id: article.id,
            description: article.sku ? `${article.name} (${article.sku})` : article.name,
            quantity: 1,
            unit_price: article.unit_price || 0,
            tax_rate: article.tax_rate ?? 21
          }
        ]
      });
    }
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const calculateTax = () => {
    return formData.items.reduce((sum, item) => {
      return sum + (item.quantity * item.unit_price * (item.tax_rate / 100));
    }, 0);
  };

  const calculateTotal = () => calculateSubtotal() + calculateTax();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id) {
      toast.error('Selecciona un cliente');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        items: formData.items.filter(item => item.description).map(item => ({
          ...item,
          quantity: parseFloat(item.quantity) || 0,
          unit_price: parseFloat(item.unit_price) || 0,
          tax_rate: parseFloat(item.tax_rate) || 21
        }))
      };

      if (isEditing) {
        await updateQuote(id, data);
        toast.success('Presupuesto actualizado');
      } else {
        await createQuote(data);
        toast.success('Presupuesto creado');
      }
      navigate('/quotes');
    } catch (e) {
      toast.error('Error al guardar presupuesto');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/quotes')}
          className="p-2 hover:bg-stone-100 rounded-xl transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-heading font-extrabold tracking-tight">
            {isEditing ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-6">
          <h2 className="text-lg font-heading font-bold mb-4">Datos Básicos</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Cliente *</Label>
                <Client360Link clientId={formData.client_id} testid="quote-form-view-360" />
              </div>
              <Select
                value={formData.client_id || undefined}
                onValueChange={(value) => setFormData({ ...formData, client_id: value })}
              >
                <SelectTrigger className="rounded-xl" data-testid="quote-client-select">
                  <SelectValue placeholder="Selecciona un cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Número de Presupuesto</Label>
              <Input
                value={formData.quote_number}
                onChange={(e) => setFormData({ ...formData, quote_number: e.target.value })}
                className="rounded-xl font-mono"
                placeholder="Se genera automáticamente"
                data-testid="quote-number-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Fecha de Emisión</Label>
              <Input
                type="date"
                value={formData.issue_date}
                onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                className="rounded-xl"
                data-testid="quote-issue-date"
              />
            </div>

            <div className="space-y-2">
              <Label>Válido Hasta</Label>
              <Input
                type="date"
                value={formData.valid_until}
                onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                className="rounded-xl"
                data-testid="quote-valid-until"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
            <h2 className="text-lg font-heading font-bold">Conceptos</h2>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              {services.length > 0 && (
                <Select onValueChange={addServiceToItems}>
                  <SelectTrigger className="w-full sm:w-48 rounded-xl">
                    <SelectValue placeholder="Añadir servicio" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name} - {formatCurrency(service.hourly_rate)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {articles.length > 0 && (
                <Select onValueChange={addArticleToItems}>
                  <SelectTrigger className="w-full sm:w-48 rounded-xl" data-testid="quote-add-article-select">
                    <SelectValue placeholder="Añadir artículo" />
                  </SelectTrigger>
                  <SelectContent>
                    {articles.map((article) => (
                      <SelectItem key={article.id} value={article.id}>
                        {article.sku ? `[${article.sku}] ` : ''}{article.name} - {formatCurrency(article.unit_price)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="hidden md:grid grid-cols-12 gap-4 text-xs uppercase tracking-[0.15em] font-bold text-stone-500 px-1">
              <div className="col-span-5">Descripción</div>
              <div className="col-span-2">Cantidad</div>
              <div className="col-span-2">Precio</div>
              <div className="col-span-2">IVA %</div>
              <div className="col-span-1"></div>
            </div>

            {formData.items.map((item, index) => (
              <div key={index} className="border border-stone-100 rounded-xl p-3 md:p-0 md:border-0 md:rounded-none">
                <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-5">
                    <Input
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      placeholder="Descripción"
                      className="rounded-xl"
                      data-testid={`quote-item-description-${index}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" step="0.01" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', e.target.value)} className="rounded-xl font-mono" />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" step="0.01" value={item.unit_price} onChange={(e) => updateItem(index, 'unit_price', e.target.value)} className="rounded-xl font-mono" />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" value={item.tax_rate} onChange={(e) => updateItem(index, 'tax_rate', e.target.value)} className="rounded-xl font-mono" />
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <button type="button" onClick={() => removeItem(index)} className="p-1.5 hover:bg-red-50 rounded-xl" disabled={formData.items.length === 1}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
                <div className="md:hidden space-y-3">
                  <div className="flex justify-between items-start">
                    <Label className="text-xs text-stone-500">Concepto {index + 1}</Label>
                    <button type="button" onClick={() => removeItem(index)} className="p-1 hover:bg-red-50 rounded-xl" disabled={formData.items.length === 1}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </div>
                  <Input value={item.description} onChange={(e) => updateItem(index, 'description', e.target.value)} placeholder="Descripción" className="rounded-xl" />
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs text-stone-500">Cant.</Label>
                      <Input type="number" step="0.01" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', e.target.value)} className="rounded-xl font-mono" />
                    </div>
                    <div>
                      <Label className="text-xs text-stone-500">Precio</Label>
                      <Input type="number" step="0.01" value={item.unit_price} onChange={(e) => updateItem(index, 'unit_price', e.target.value)} className="rounded-xl font-mono" />
                    </div>
                    <div>
                      <Label className="text-xs text-stone-500">IVA %</Label>
                      <Input type="number" value={item.tax_rate} onChange={(e) => updateItem(index, 'tax_rate', e.target.value)} className="rounded-xl font-mono" />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <Button type="button" variant="outline" onClick={addItem} className="rounded-xl">
              <Plus className="w-4 h-4 mr-2" />
              Añadir Concepto
            </Button>
          </div>

          <div className="mt-6 pt-6 border-t border-stone-100 flex justify-end">
            <div className="w-64 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-stone-500">Subtotal</span>
                <span className="font-mono">{formatCurrency(calculateSubtotal())}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-stone-500">IVA</span>
                <span className="font-mono">{formatCurrency(calculateTax())}</span>
              </div>
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-stone-100">
                <span>Total</span>
                <span className="font-mono text-[#FF6B4A]">{formatCurrency(calculateTotal())}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-6">
          <h2 className="text-lg font-heading font-bold mb-4">Notas</h2>
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Notas adicionales..."
            className="rounded-xl resize-none"
            rows={3}
            data-testid="quote-notes-input"
          />
        </div>

        <div className="flex gap-4 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate('/quotes')} className="rounded-xl">
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300"
            data-testid="save-quote-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando...' : (isEditing ? 'Guardar' : 'Crear Presupuesto')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default QuoteFormPage;
